import { useSyncExternalStore } from "react";
import type { ScreenMode } from "@/density/derive";

export type Screen = "densities" | "alerts" | "library" | "settings";

/**
 * Повод открыть страницу «Настройки» (design.md stage9-settings-page, Д1):
 * `terminal` — раздел «Терминал» в поле зрения, `groups` — то же плюс
 * пояснение и фокус в поле групп; `null` — страница сверху.
 */
export type SettingsReason = "terminal" | "groups";

/**
 * Маршрут: экран, для плотностей — вид, для настроек — повод (web-navigation,
 * §9 1.48, §61 п. 5, §127 п. 7).
 */
export type Route =
  | { screen: "densities"; view: ScreenMode }
  | { screen: "alerts" | "library" }
  | { screen: "settings"; reason: SettingsReason | null };

/** Адрес каждого экрана; `densities` — корень, он же адрес по умолчанию. */
const SCREEN_HASHES = {
  densities: "#/",
  alerts: "#/alerts",
  library: "#/library",
  settings: "#/settings",
} satisfies Record<Screen, string>;

/** Адреса поводов страницы «Настройки»; страница без повода — `SCREEN_HASHES.settings`. */
const SETTINGS_REASON_HASHES = {
  terminal: "#/settings/terminal",
  groups: "#/settings/terminal/groups",
} satisfies Record<SettingsReason, string>;

/**
 * Адрес каждого вида экрана плотностей. Таблица внутри называется
 * `densities` (`ScreenMode`), в адресе — `table`: `ScreenMode` не
 * переименовывается (design.md stage8-sidebar-views, Д1), соответствие
 * живёт здесь, рядом с разбором.
 */
const DENSITY_VIEW_HASHES = {
  cards: "#/cards",
  coins: "#/coins",
  densities: "#/table",
  situations: "#/situations",
} satisfies Record<ScreenMode, string>;

/** Те же адреса списком пар — для разбора, без приведения ключей к `ScreenMode`. */
const DENSITY_VIEW_ENTRIES: ReadonlyArray<readonly [ScreenMode, string]> = [
  ["cards", DENSITY_VIEW_HASHES.cards],
  ["coins", DENSITY_VIEW_HASHES.coins],
  ["densities", DENSITY_VIEW_HASHES.densities],
  ["situations", DENSITY_VIEW_HASHES.situations],
];

/**
 * Маршрут из адреса §9 (1.20, 1.48; web-navigation «Открытый экран
 * переживает перезагрузку вкладки»). Корень `#/` — карточки. Неизвестное
 * значение, в том числе вложенное `#/densities/table`, — плотности,
 * карточки: опечатка ведёт туда же, а не на пустой экран.
 */
export function routeFromHash(hash: string): Route {
  if (hash === SCREEN_HASHES.alerts) {
    return { screen: "alerts" };
  }
  if (hash === SCREEN_HASHES.library) {
    return { screen: "library" };
  }
  if (hash === SCREEN_HASHES.settings) {
    return { screen: "settings", reason: null };
  }
  if (hash.startsWith(`${SCREEN_HASHES.settings}/`)) {
    // Любой подадрес — страница; поводом он становится, только если назван.
    if (hash === SETTINGS_REASON_HASHES.terminal) {
      return { screen: "settings", reason: "terminal" };
    }
    if (hash === SETTINGS_REASON_HASHES.groups) {
      return { screen: "settings", reason: "groups" };
    }
    return { screen: "settings", reason: null };
  }
  for (const [view, viewHash] of DENSITY_VIEW_ENTRIES) {
    if (hash === viewHash) {
      return { screen: "densities", view };
    }
  }
  return { screen: "densities", view: "cards" };
}

/** Экран из адреса — экранная половина маршрута. */
export function screenFromHash(hash: string): Screen {
  return routeFromHash(hash).screen;
}

/** Вид из адреса; `null` — открыт другой экран, адрес вида не называет. */
export function densityViewFromHash(hash: string): ScreenMode | null {
  const route = routeFromHash(hash);
  return route.screen === "densities" ? route.view : null;
}

/** Обратное преобразование — для ссылок колонки навигации. */
export function hashOfScreen(screen: Screen): string {
  return SCREEN_HASHES[screen];
}

/** Адрес страницы «Настройки» с поводом; без повода — страница сверху. */
export function hashOfSettingsReason(reason: SettingsReason | null): string {
  return reason === null ? SCREEN_HASHES.settings : SETTINGS_REASON_HASHES[reason];
}

/** Адрес вида экрана плотностей — для ссылок подпунктов панели. */
export function hashOfDensityView(view: ScreenMode): string {
  return DENSITY_VIEW_HASHES[view];
}

/**
 * Вид, который рисует экран плотностей. Пока он открыт, вид задаёт адрес;
 * пока открыт другой экран, адрес вида не называет, и скрытому экрану
 * остаётся последний нарисованный. Выбор вида при переходе по адресу этой
 * функцией не делается — только то, что уже на скрытом экране.
 */
export function shownDensityView(fromHash: ScreenMode | null, lastShown: ScreenMode): ScreenMode {
  return fromHash ?? lastShown;
}

function subscribe(callback: () => void): () => void {
  window.addEventListener("hashchange", callback);
  return () => window.removeEventListener("hashchange", callback);
}

function getScreenSnapshot(): Screen {
  return screenFromHash(window.location.hash);
}

function getDensityViewSnapshot(): ScreenMode | null {
  return densityViewFromHash(window.location.hash);
}

function getSettingsReasonSnapshot(): SettingsReason | null {
  const route = routeFromHash(window.location.hash);
  return route.screen === "settings" ? route.reason : null;
}

/**
 * Текущий экран клиента, читаемый из `location.hash` и обновляемый по
 * `hashchange` — переход, перезагрузка и «назад» браузера дают одно и то же
 * состояние без второго источника правды (design.md, решение 1).
 */
export function useScreen(): Screen {
  return useSyncExternalStore(subscribe, getScreenSnapshot);
}

/** Вид экрана плотностей из того же адреса; `null`, пока открыт другой экран. */
export function useDensityView(): ScreenMode | null {
  return useSyncExternalStore(subscribe, getDensityViewSnapshot);
}

/** Повод страницы «Настройки» из того же адреса; `null` — без повода или открыт другой экран. */
export function useSettingsReason(): SettingsReason | null {
  return useSyncExternalStore(subscribe, getSettingsReasonSnapshot);
}
